import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MeasurementSystem,
  TimelineEntryConnection,
  TimelineEntry as TimelineEntryModel,
  User,
} from '../../../../types/__generated__';
import useDeleteTimelineEntry from '../../../hooks/timeline/useDeleteTimelineEntry';
import LoadMoreButton from '../../LoadMoreButton/LoadMoreButton';
import { StravaTimelineEntry } from '../StravaTimelineEntry/StravaTimelineEntry';
import TimelineEntry from '../TimelineEntry/TimelineEntry';
import { TimelineContext } from '../TimelineContext';
import { useNotNullableContext } from '../../../hooks/useNotNullableContext';

function getTimelineEntryComponent(timelineEntry: TimelineEntryModel) {
  if (timelineEntry.type === 'FITNESS' && timelineEntry.fitnessActivity) {
    return StravaTimelineEntry;
  }
  return TimelineEntry;
}

interface TimelineProps {
  timeline?: Partial<TimelineEntryConnection>;
  owner?: Partial<User>;
  measurementSystem?: MeasurementSystem;
}

const Timeline: FunctionComponent<TimelineProps> = ({
  timeline,
  owner,
  measurementSystem,
}) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('common', { useSuspense: false });
  const [deleteTimelineEntry] = useDeleteTimelineEntry(measurementSystem);
  const { fetchNextPage } = useNotNullableContext(TimelineContext);

  if (!timeline) {
    return null;
  }
  const { nodes = [], pageInfo } = timeline;
  const { hasNextPage } = pageInfo ?? {};

  async function handleFetchMore() {
    // Using state because Apollo does not render component with final data
    // with notifyOnNetworkStatus https://github.com/apollographql/apollo-client/issues/4574
    setLoading(true);
    await fetchNextPage();
    setLoading(false);
  }

  return (
    <div data-qa="timeline">
      {nodes.map((timelineEntry: TimelineEntryModel) => {
        let TimelineEntryComponent = getTimelineEntryComponent(timelineEntry);
        return (
          <TimelineEntryComponent
            key={timelineEntry.id}
            timelineEntry={timelineEntry}
            avatarUrl={owner?.avatar ?? undefined}
            postedBy={owner?.name ?? ''}
            onDeleteEntry={deleteTimelineEntry}
          />
        );
      })}
      {hasNextPage && (
        <LoadMoreButton
          loading={loading}
          onClick={handleFetchMore}
          trackingData={{
            action_name: 'click',
            subtype: 'button',
            event: 'click',
            event_value: 'show more',
            sub_section: 'timeline',
          }}
        >
          {t('common.showMore', 'Show more')}
        </LoadMoreButton>
      )}
    </div>
  );
};

export default Timeline;
